// npm imports
import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// component imports
import { Toastr } from "@revvsales/components";
import AddEditDropdown from "./AddEditDropdown";
import DropdownListItem from "./DropdownListItem";

// action imports
import {
  getAssetStatusListAction,
  getAssetTagListAction,
  updateAssetStatusListAction,
  updateAssetTagsListAction,
  updateAssestStatusAction,
  updateAssetTagAction,
} from "../../../actions/AssetActions/assetActions";
import {
  updateTemplateTagAction,
  assignUserGroupToTemplateAction,
  getTemplateInitData,
} from "../../../actions/DocumentTemplateActions/templateActions";
import { fetchUserGroupsService } from "../../../actions/UserActions/userService";
// internal imports
import { EVENTS } from "../../../constants";
import { GlobalUtils, AmplitudeUtils } from "../../../utils";

// destructuring
const { amplitudeLogEvent } = AmplitudeUtils;
const { handleApiErrMsg, truncateString } = GlobalUtils;

class DropdownContainer extends Component {
  state = {
    showAddEditContainer: false,
    showLoader: false,
  };

  componentDidMount() {
    document.addEventListener("click", this.handleClickOutside);
    this._initAssetStatusTagList();
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  }

  /**
   * ::::: INIT_ASSET_STATUS_OR_TAG_LIST :::::
   */
  _initAssetStatusTagList = () => {
    const { type, assetStatusList, assetTagsList } = this.props;
    let decideActionType = null;
    if (
      type === "status" &&
      (assetStatusList && assetStatusList.length === 0)
    ) {
      decideActionType = this.props.getAssetStatusListAction;
    } else if (
      type === "tags" &&
      (assetTagsList && assetTagsList.length === 0)
    ) {
      decideActionType = this.props.getAssetTagListAction;
    } else if (type === "userGroups") {
      decideActionType = fetchUserGroupsService;
    }
    if (decideActionType) {
      this.setState({ showLoader: true });
      decideActionType()
        .then(() => {
          this.setState({ showLoader: false });
        })
        .catch(error => {
          console.debug(
            `DropdownContainer => _initAssetStatusTagList => => Error : ${error}`,
          );
          Toastr.showToastr({
            type: "failure",
            message: handleApiErrMsg(error),
            timeout: 3000,
          });
          this.setState({ showLoader: false });
        });
    }
  };

  setWrapperRef = node => {
    this.wrapperRef = node;
  };

  handleClickOutside = event => {
    event.stopPropagation();
    if (
      this.wrapperRef &&
      event.target &&
      event.target.dataset &&
      this.wrapperRef.id !== event.target.dataset.id &&
      !this.wrapperRef.contains(event.target)
    ) {
      this.props.closeDropdownContainer();
    }
  };

  toggleAddEditContainer = () => {
    const { showAddEditContainer } = this.state;
    this.setState({ showAddEditContainer: !showAddEditContainer });
  };

  closeAddEditContainer = () => {
    const { showAddEditContainer } = this.state;
    if (showAddEditContainer) {
      this.setState({ showAddEditContainer: false });
    }
  };

  // make state changes permanent in both containers(from add edit)
  _applyChanges = newList =>
    new Promise((resolve, reject) => {
      const { type } = this.props;
      const newListWithSequence = newList.map((item, index) => ({
        ...item,
        sequence: index,
      }));
      if (type === "status") {
        // make changes to redux state: call update status list service and dispatcher
        this.props
          .updateAssetStatusList(newListWithSequence)
          .then(() => {
            resolve(this.props.assetStatusList);
          })
          .catch(() => {
            reject();
          })
          .finally(() => {
            this.toggleAddEditContainer();
          });
      } else if (type === "tags") {
        // dispatcher for update
        this.props
          .updateAssetTagsList(newListWithSequence)
          .then(() => {
            resolve(this.props.assetTagsList);
          })
          .catch(() => {
            reject();
          })
          .finally(() => {
            this.toggleAddEditContainer();
          });
      }
    });

  _changeDocumentStatus = (docID, item, type, templateId) => {
    if (type === "userGroups") {
      const data = [];
      const stateData = [];
      let newData = [];
      if (
        this.props.templateUsergroups &&
        this.props.templateUsergroups.length > 0
      ) {
        this.props.templateUsergroups.forEach(users => {
          stateData.push(users);
          data.push(users.id);
        });
      }
      if (item) {
        newData = stateData.filter(i => i.id !== item.id);
        newData.push(item);
        data.push(item.id);
      }
      const usergroupIds = { usergroup_ids: data };

      this.props
        .assignUserGroupToTemplateAction(templateId, usergroupIds, newData)
        .then(() => {
          // this.props.closeDropdownContainer();
        })
        .catch(error => {
          console.error(
            "DropdownContainer => _changeUserGroup => assignUserGroupToTemplateAction => Error : ",
            error,
          );
        });

      // this._onInitTemplaes();
    } else if (docID) {
      if (type === "status") {
        const amplitudeObject = {
          oldStatus: this.props.documentStatus
            ? this.props.documentStatus.name
            : "",
          newStatus: item ? item.name : "",
          tag: this.props.documentTags ? this.props.documentTags.name : "",
          initiatedFrom: "Asset",
        };
        this.props
          .updateAssestStatusAction(docID, item)
          .then(() => {
            amplitudeLogEvent(
              EVENTS.EVENT_NAMES.STATUS_CHANGED,
              amplitudeObject,
            );
            this.props.closeDropdownContainer();
          })
          .catch(error => {
            console.error(
              "DropdownContainer => _changeDocumentStatus => updateAssestStatusAction => Error : ",
              error,
            );
          });
      } else if (type === "tags") {
        const amplitudeObject = {
          oldTag: this.props.documentTags && this.props.documentTags.name,
          newTag: item ? item.name : "",
          status: this.props.documentStatus && this.props.documentStatus.name,
        };
        this.props
          .updateAssetTagAction(docID, item)
          .then(() => {
            amplitudeLogEvent(EVENTS.EVENT_NAMES.TAGS_CHANGED, amplitudeObject);
            this.props.closeDropdownContainer();
          })
          .catch(error => {
            console.error("error", error);
          });
      }
    } else if (templateId) {
      const amplitudeObject = {
        oldTag: this.props.templateTag && this.props.templateTag.name,
        newTag: item ? item.name : "",
        TemplateID: this.props.templateId,
      };
      this.props
        .updateTemplateTagAction(templateId, item)
        .then(() => {
          amplitudeLogEvent(
            EVENTS.EVENT_NAMES.TEMPLATE_TAGS_CHANGED,
            amplitudeObject,
          );
          this.props.closeDropdownContainer();
        })
        .catch(error => {
          console.error(
            "DropdownContainer => _changeDocumentStatus => updateTemplateTagAction => Error : ",
            error,
          );
        });
    }
  };

  deleteUserGroup = (item, templateId) => {
    const data = [];
    const stateData = [];
    if (
      this.props.templateUsergroups &&
      this.props.templateUsergroups.length > 0
    ) {
      this.props.templateUsergroups.forEach(users => {
        stateData.push(users);
        data.push(users.id);
      });
    }
    const tempState = stateData.filter(i => i.id !== item.id);
    const temp = data.filter(i => i !== item.id);

    const usergroupIds = { usergroup_ids: [...temp] };
    this.props.assignUserGroupToTemplateAction(templateId, usergroupIds, [
      ...tempState,
    ]);
  };

  _addNewUsergroup = () => {
    const currentUrl = this.removeLastDirectoryPartOf(window.location.href);
    window.location.replace(`${currentUrl}/${"settings"}/${"teammates"}`);
  };

  removeLastDirectoryPartOf = url => {
    const newUrl = url.split("/");
    newUrl.pop();
    return newUrl.join("/");
  };

  render() {
    const {
      docID,
      showMenu,
      className,
      id,
      type,
      assetStatusList,
      assetTagsList,
      templateId,
      userGroups,
    } = this.props;
    const { showAddEditContainer, showLoader } = this.state;
    let listItems = assetTagsList;
    let addEditLabel = "Manage Tags";
    if (type === "status") {
      listItems = assetStatusList;
      addEditLabel = "Manage Status";
    } else if (type === "userGroups") {
      listItems = userGroups;
      addEditLabel = "New users groups";
    }
    // eslint-disable-next-line no-console
    console.log("listItems", listItems);
    return (
      <div
        id={id}
        ref={this.setWrapperRef}
        className={`dropdowncontainer-menu ${
          showMenu ? "show-dropdown-menu" : ""
        } ${type === "userGroups" ? "user-group-width" : ""}`}>
        {/* ------- Loading text section ------- */}
        {showLoader && (
          <div className="dropdowncontainer-loading-text">loading...</div>
        )}
        <div className="dropdown-inner-container ">
          <div className={`${showAddEditContainer ? "hide-tag-menu" : ""}`}>
            {/* ------- List items section ------- */}
            <ul className="dropdowncontainer-list">
              {type === "userGroups" && listItems.length > 0 ? (
                <Fragment>
                  {this.props.templateUsergroups.length > 0 ? (
                    <Fragment>
                      <div className="dropdown-list-item-usergroup">
                        {this.props.templateUsergroups.map(item => (
                          <div
                            className="dropdown-user"
                            onClick={() =>
                              this.deleteUserGroup(item, templateId)
                            }
                            onKeyPress={() => {}}
                            role="button"
                            tabIndex="0">
                            {item.name
                              ? truncateString(item.name, 17, true)
                              : null}
                            <i className="close-icon">
                              <FontAwesomeIcon icon={["far", "times"]} />
                            </i>
                          </div>
                        ))}
                      </div>
                      <div className="divide-line" />
                    </Fragment>
                  ) : (
                    <Fragment>
                      <div className="dropdown-list-item-empty">
                        Select a group
                      </div>
                    </Fragment>
                  )}
                </Fragment>
              ) : null}
              {type === "userGroups" && listItems.length <= 0 ? (
                <Fragment>
                  <div className="dropdown-list-item-empty">
                    <FontAwesomeIcon
                      icon={["far", "exclamation-circle"]}
                      className="dropdown-icon-empty"
                    />
                    You do not have any <br />
                    User Groups created.
                  </div>
                </Fragment>
              ) : (
                listItems &&
                listItems.map(item => (
                  <DropdownListItem
                    docID={docID}
                    templateId={templateId}
                    className="dropdowncontainer-list-item"
                    type={type}
                    item={item}
                    key={`${item.id}-${type}listitem${id}`}
                    changeDocumentStatus={this._changeDocumentStatus}
                    onKeyPress={() => {}}
                  />
                ))
              )}
              {type === "userGroups" ? (
                <div
                  onClick={this._addNewUsergroup}
                  onKeyPress={() => {}}
                  data-id={`addedit${id}`}
                  role="button"
                  tabIndex="0"
                  className="add-another-usergroup-wrapper">
                  <i
                    className="plusicon"
                    data-test="dropdowncontainer-new-user-btn">
                    <FontAwesomeIcon icon={["far", "plus"]} className="" />
                  </i>
                  <div className="add-another-user-btn">
                    <div className="add-another-user-title">
                      New users groups
                    </div>
                  </div>
                </div>
              ) : null}
            </ul>
            {type === "userGroups" ? (
              <div className="add-another-usergroup-footer">
                <div className="add-another-usergroup-wrapper">
                  <div className="add-another-user-btn">
                    <div className="add-another-user-desc">
                      <span
                        className="addeditdropdown-drag-icon"
                        role="img"
                        aria-label="img">
                        💡
                      </span>
                      Only users belonging <br />
                      to the selected user <br />
                      groups can view and use <br />
                      this template here
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                onClick={this.toggleAddEditContainer}
                onKeyPress={() => {}}
                data-id={`addedit${id}`}
                role="button"
                tabIndex="0"
                className="dropdowncontainer-addedit-section add-edit-item">
                <i>
                  <FontAwesomeIcon icon={["far", "edit"]} />
                </i>

                {addEditLabel}
              </div>
            )}
            {/* {type === "userGroups" ? (
              <div>
                <span role="img" aria-label="img">
                  💡
                </span>
                Only people belonging to these user groups can view and use this
                template. Learn more here
              </div>
            ) : null} */}
          </div>

          {showAddEditContainer && (
            <AddEditDropdown
              id={`addedit${id}`}
              closeAddEditContainer={this.closeAddEditContainer}
              type={type}
              listItems={listItems}
              showMenu={showAddEditContainer}
              className={className}
              applyListChanges={this._applyChanges}
            />
          )}
        </div>
      </div>
    );
  }
}

DropdownContainer.propTypes = {
  type: PropTypes.string.isRequired,
  showMenu: PropTypes.bool.isRequired,
  closeDropdownContainer: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

const mapStateToProps = ({
  AssetStore: { assetStatusList, assetTagsList },
  SettingsStore: { userGroups },
}) => ({
  assetStatusList,
  assetTagsList,
  userGroups,
});

const mapDispatchToProps = {
  getAssetStatusListAction,
  getAssetTagListAction,
  updateAssestStatusAction,
  updateTemplateTagAction,
  updateAssetTagAction,
  updateAssetStatusList: updateAssetStatusListAction,
  updateAssetTagsList: updateAssetTagsListAction,
  assignUserGroupToTemplateAction,
  getTemplateInitData,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DropdownContainer);



// WEBPACK FOOTER //
// ./src/components/AssetWrapper/DropdownContainer/index.js